// Lesson 1.2, Safe by Default.
//
// This is the lesson Leena approved in the AI courses handoff (2026-10-08,
// section 8). It was converted to this structure MECHANICALLY: the prose,
// the never-paste list, the quick-check answers and the prompt card are the
// approved words. No silent improvements, per the standing build rule.
//
// NOT YET VERIFIED: the three settings paths under `tabs` carry
// `verify: true`. Every tool setting must be checked against the live product
// before this lesson is published, and the renderer shows an agency-only
// warning strip while that flag is set.

export default {
  key: '1-2',
  title: 'Safe by default',
  minutes: 18,
  status: 'ready',
  outcome: 'What never goes in, training off and memory on, and catching confident wrong answers.',

  why: 'AI tools are only as safe as what you put into them. Three habits cover almost all the risk. Lock them in now and you will not have to think twice about it again.',

  blocks: [
    {
      type: 'concept',
      heading: 'The concept: training vs. memory',
      body: [
        { type: 'p', text: 'There are two different kinds of "learning" in an AI tool, and it pays to keep them separate.' },
        { type: 'deflist', items: [
          { term: 'Training', text: 'The company may use your chats to improve its future models. For work accounts, turn it off.' },
          { term: 'Memory', text: 'Your AI remembers your preferences and context across chats. Keep it on, because that is what makes the AI know you.' },
        ] },
        { type: 'rule', text: 'Let AI learn about you, not from you.' },
      ],
    },

    {
      type: 'concept',
      heading: 'The never-paste list',
      body: [
        { type: 'p', text: 'Never paste any of the following into an AI tool:' },
        { type: 'list', items: [
          'passwords, logins or API keys',
          'card numbers, bank details, SSNs or ID numbers',
          'client personal details such as home addresses, phone numbers or health information',
          'unreleased financials',
          'anything under an NDA, or that a client asked you to keep confidential',
        ] },
        { type: 'p', text: 'Grey areas are fine once you swap the specifics. Change "Sarah Johnson at 14 Elm St wants $42,000 off" to "[Client] wants a discount on a [project]."' },
      ],
    },

    {
      type: 'concept',
      heading: 'Confident wrong answers',
      body: [
        { type: 'p', text: 'AI tools rarely say "I am not sure." They can make things up fluently: numbers, quotes, policies, laws and links.' },
        { type: 'callout', tone: 'rule', text: 'Use a 30-second check. Any number, name, date, quote or link you are about to send gets checked against the source first.' },
      ],
    },

    {
      type: 'tabs',
      heading: 'Try it: turn training off',
      verify: true,
      intro: 'Pick your tool. These are the only steps in this lesson where the clicks differ.',
      tabs: [
        { tool: 'ChatGPT', steps: [
          'Settings, then Data Controls, then turn off "Improve the model for everyone."',
          'For one-off sensitive work, use a Temporary Chat.',
        ] },
        { tool: 'Claude', steps: [
          'Settings, then Privacy, then turn off "Help improve Claude."',
          'For one-off sensitive work, use an incognito chat.',
        ] },
        { tool: 'Gemini', steps: [
          'Activity settings, then turn off Keep Activity, and use Temporary Chat.',
          'In Gemini, turning activity off also limits personalization.',
        ] },
      ],
      callout: { tone: 'info', text: 'Business, team and workspace plans generally do not train on your data by default. Worth knowing when your company decides which plan to pay for.' },
    },

    {
      type: 'exercise',
      heading: 'Your turn',
      intro: 'On your own real work, not a practice example.',
      steps: [
        { id: 'flip_setting', text: 'Flip the training setting in your own tool. Tick the box when done.' },
        { id: 'rewrite_real', text: 'Pick a real email or document you would like AI help with this week. Rewrite it with placeholders until it passes the never-paste list.' },
      ],
    },

    {
      type: 'quickcheck',
      questions: [
        {
          q: 'Your AI remembers you prefer short replies. Is that training or memory?',
          options: ['Training', 'Memory'],
          answer: 1,
          because: 'Memory is about you, and it is the part worth keeping on.',
        },
        {
          q: 'A client sends their account login so you can "check something." Can it go into AI?',
          options: ['Yes, if the chat is temporary', 'No'],
          answer: 1,
          because: 'Logins are on the never-paste list, whatever the chat setting says.',
        },
        {
          q: 'AI gives you a statistic for a proposal. What do you do before sending?',
          options: ['Check it against the source', 'Ask the AI if it is sure', 'Send it, the AI sounded confident'],
          answer: 0,
          because: 'Asking the AI again gets you another confident answer, not a verified one.',
        },
      ],
    },

    {
      type: 'promptcard',
      title: 'Fact-check yourself',
      body: 'Before I use this, list every number, name, date, quote and link in your answer. Rate how confident you are in each one, and flag anything I should verify against a source.',
    },

    {
      type: 'takeaway',
      text: 'Let AI learn about you, not from you, and check any fact before it leaves your hands.',
    },
  ],

  // The renderer appends the "What's missing?" box and the hidden video slot to
  // every lesson, so they are not listed here. This is the note for the slot.
  videoNote: 'Leena walks through the settings.',
};
